import { z } from 'zod';

import {
  DATA_PREFIX,
  EXTRACT_MIME_TYPE_FROM_DATA_URL,
} from '@/components/RichTextEditor/constants';
import { convertFileToBase64 } from '@/components/RichTextEditor/utils/RichTextEditor.utils';
import {
  MessageContentPart,
  MessageContentPartImageUrl,
  MessageContentPartLangChainMultimodal,
  MessageContentPartOpenAIFile,
  MessageContentPartText,
  zMessageContentPartImageUrl,
  zMessageContentPartLangChainMultimodal,
  zMessageContentPartOpenAIFile,
  zMessageContentPartText,
} from '@/types/schema';

import { SUPPORTED_FILE_TYPES } from '../src/constants';

const getDefaultFilename = (suffix: string) => `file-${suffix}`;

export const constructFileFromMessageContentPart = (
  c:
    | MessageContentPartImageUrl
    | MessageContentPartOpenAIFile
    | MessageContentPartLangChainMultimodal,
  index: number
) => {
  const rawData = 'data' in c ? c.data : undefined;
  const fileData = 'file' in c ? c.file.file_data : undefined;
  const imageUrl =
    'image_url' in c
      ? typeof c.image_url === 'string'
        ? c.image_url
        : c.image_url.url
      : undefined;
  const urlData = 'url' in c ? c.url : undefined;
  const data = rawData ?? fileData ?? imageUrl ?? urlData;
  if (!data) {
    throw new Error('No data found for file');
  }
  const mimeType = 'mime_type' in c ? c.mime_type : undefined;
  const type =
    mimeType ??
    (data.startsWith(DATA_PREFIX)
      ? EXTRACT_MIME_TYPE_FROM_DATA_URL(data)
      : c.type);

  const name =
    'metadata' in c
      ? c.metadata?.filename ?? getDefaultFilename(index.toString())
      : 'file' in c
      ? c.file.filename
      : getDefaultFilename(index.toString());
  return {
    name,
    type,
    data,
  };
};

// Returns a Promise of a typed multimodal block for images or PDFs
export const fileToContentBlock = async (
  file: File
): Promise<MessageContentPartLangChainMultimodal> => {
  if (!SUPPORTED_FILE_TYPES.includes(file.type)) {
    return Promise.reject(new Error(`Unsupported file type: ${file.type}`));
  }

  const data = await convertFileToBase64(file);

  return {
    type: file.type.startsWith('image') ? 'image' : 'file',
    data: data.split(',')[1],
    metadata: {
      filename: file.name,
    },
    source_type: 'base64',
    mime_type: file.type,
  };
};

const zFileContentPart = z.union([
  zMessageContentPartImageUrl,
  zMessageContentPartOpenAIFile,
  zMessageContentPartLangChainMultimodal,
]);

// Helper function to safely extract file content parts
export const extractFileContentParts = (
  content: MessageContentPart[]
): (
  | MessageContentPartImageUrl
  | MessageContentPartOpenAIFile
  | MessageContentPartLangChainMultimodal
)[] => {
  return content
    .filter((c) => {
      const result = zFileContentPart.safeParse(c);
      return result.success;
    })
    .map((c) => zFileContentPart.parse(c));
};

export const extractMessageContentParts = (
  content: MessageContentPart[]
): MessageContentPartText[] => {
  return content
    .filter((c) => {
      const result = zMessageContentPartText.safeParse(c);
      return result.success;
    })
    .map((c) => zMessageContentPartText.parse(c));
};
